import { ref, computed, readonly, getCurrentInstance } from 'vue'
import { defineStore } from 'pinia'
import { useI18n } from 'vue-i18n'

import api from '@/utils/api'

import Task from '@/models/Task.model'
import { TASK_PAGINATION_LIMIT } from '@/configs/config'

export const useTasksStore = defineStore('tasksStore', () => {
  const instance = getCurrentInstance()
  const { $api, $toast, $modal, $bus } = instance.appContext.config.globalProperties
  const { t } = useI18n()

  const tasks = ref([])
  const selectedTasks = ref([])

  const loading = ref(false)
  const nextPage = ref(null)

  const selectedTaskType = ref('encoding')
  const sortOptions = ref({
    sort: '-time',
    limit: TASK_PAGINATION_LIMIT
  })

  const finishedTasks = computed(() => {
    return tasks.value.filter(task =>
      task.status !== 'Progress'
    )
  })
  const unfinishedTasks = computed(() => {
    return tasks.value.filter(task =>
      task.status !== 'Completed' && task.status !== 'Error'
    )
  })

  const setTasks = (array) => {
    tasks.value = array
  }
  const getTasks = () => {
    return tasks.value
  }

  const setNextPage = (page) => {
    nextPage.value = page
  }

  const showLoader = () => {
    loading.value = true
  }
  const hideLoader = () => {
    loading.value = false
  }

  const fetchTasks = async (params, meta) => {
    if (loading.value) return
    showLoader()

    let tasksCategory = 'transcoder_tasks'
    switch (selectedTaskType.value) {
      case 'download': tasksCategory = 'download_tasks'
        break
      case 's3multipart': tasksCategory = 's3multipart'
        break
      default:
        tasksCategory = 'transcoder_tasks'
    }

    if (tasksCategory === 's3multipart') {
      hideLoader()
      return
    }

    const requestParams = {
      ...sortOptions.value,
      ...params
    }

    try {
      const { data } = await api.getTasks({ category: tasksCategory, ...requestParams })
      setNextPage(data.next)
      const newTasksArray = data.tasks?.map(task =>
        new Task({ ...task, title: task.title.split(' ').splice(1).join(' ') })
      ) || []

      if (meta === 'more') setTasks(getTasks().concat(newTasksArray))
      else setTasks(newTasksArray)
    } catch {
      $toast.show({
        text: t('При загрузке произошла ошибка')
      })
    } finally {
      hideLoader()
      refreshTask()
    }
  }
  const refreshTask = () => {
    if (unfinishedTasks.value.length === 0) return

    let refreshTimer = null

    const updateUnfinishedTask = async () => {
      const unfinishedTask = unfinishedTasks.value[0]
      if (!unfinishedTask) {
        clearTimeout(refreshTimer)
        refreshTimer = null
        return
      }

      try {
        const { data } = await api.getTask({
          id: unfinishedTask.id,
          category: unfinishedTask.category === 'encoding' ? 'transcoder_tasks' : 'download_tasks'
        })
        const newTask = new Task({
          ...data.task,
          title: data.task.title.split(' ').slice(1).join(' ')
        })
        const taskIndex = tasks.value.findIndex(item => item.id === data.task.id)
        if (taskIndex !== -1) {
          const newTasks = [...tasks.value]
          newTasks[taskIndex] = newTask
          tasks.value = newTasks
        }
      } finally {
        if (unfinishedTasks.value.length > 0) {
          refreshTimer = setTimeout(updateUnfinishedTask, 2500)
        }
      }
    }

    clearTimeout(refreshTimer)
    refreshTimer = unfinishedTasks.value.length > 0
      ? setTimeout(updateUnfinishedTask, 2500)
      : null
  }
  const removeTasks = async () => {
    if (selectedTasks.value.length === 0) return

    const removedTasksIds = []
    let hasRejected = false

    for (const taskId of selectedTasks.value) {
      try {
        await $api.deleteTask({
          id: taskId,
          category: selectedTaskType.value === 'encoding' ? 'transcoder_tasks' : 'download_tasks'
        })
        removedTasksIds.push(taskId)
        setTasks(tasks.value.filter(t => t.id !== taskId))
      } catch {
        hasRejected = true
      }
    }

    selectedTasks.value = selectedTasks.value.filter(t => !removedTasksIds.includes(t))

    $toast.show({
      text: hasRejected ? t('При удалении произошла ошибка') : t('Задачи удалены'),
      lifetime: 1000
    })
  }
  const toggleTaskSelect = (e) => {
    const taskIndex = selectedTasks.value.findIndex(taskId => taskId === e.target.id)

    if (taskIndex >= 0) {
      selectedTasks.value.splice(taskIndex, 1)
    } else selectedTasks.value.push(e.target.id)
  }
  const toggleAllTaskSelect = () => {
    selectedTasks.value = finishedTasks.value.length === selectedTasks.value.length
      ? []
      : finishedTasks.value.map(t => t.id)
  }

  return {
    tasks: readonly(tasks),
    selectedTasks,
    finishedTasks,
    unfinishedTasks,
    loading,
    nextPage,
    selectedTaskType,
    sortOptions,
    setTasks,
    getTasks,
    setNextPage,
    showLoader,
    hideLoader,
    fetchTasks,
    refreshTask,
    toggleTaskSelect,
    toggleAllTaskSelect,
    removeTasks
  }
})
